// Mijn profiel (Gebruiker self-service; PLAN-W28; FRONTEND-BLUEPRINT §3.8). The signed-in user views +
// edits their own profile (full name, preferred name, UI language). Like the Team surface this talks to
// the auth-worker (AUTH_BASE) credentialed — the `user` registry is owner-only — so it works behind real
// auth. Email is WorkOS identity → read-only. Saving the language also flips the app locale immediately.
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n, type Lang } from "../../lib/i18n";
import { AUTH_BASE } from "../../lib/env";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Profile {
  id: string; email: string; fullName: string | null; preferredName: string | null;
  preferredLanguage: string; emailVerified: boolean;
}

async function authFetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    ...init, credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) throw new Error(typeof body === "object" && body && "error" in body ? String((body as Record<string, unknown>).error) : `HTTP ${res.status}`);
  return body;
}

export function MijnProfielRoute() {
  const { t, setLang } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["me-profile"], queryFn: () => authFetch("/me/profile") as Promise<Profile> });

  const [fullName, setFullName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [language, setLanguage] = useState<Lang>("nl");
  // hydrate the form once the profile loads
  useEffect(() => {
    if (data) {
      setFullName(data.fullName ?? "");
      setPreferredName(data.preferredName ?? "");
      setLanguage((data.preferredLanguage === "en" ? "en" : "nl"));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => authFetch("/me/profile", {
      method: "PATCH",
      body: JSON.stringify({ fullName: fullName.trim() || null, preferredName: preferredName.trim() || null, preferredLanguage: language }),
    }),
    onSuccess: () => {
      setLang(language); // reflect the saved UI language immediately
      qc.invalidateQueries({ queryKey: ["me-profile"] });
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("profile.title")} description={t("profile.sub")} />
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {data && (
        <Card className="max-w-xl">
          <CardHeader><CardTitle>{t("profile.title")}</CardTitle></CardHeader>
          <CardContent>
            <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
              <div className="grid gap-1.5">
                <Label htmlFor="pf-email">{t("profile.email")}</Label>
                <Input id="pf-email" value={data.email} readOnly disabled />
                <p className="text-xs text-muted-foreground">{t("profile.email.readonly")}</p>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="pf-fullname">{t("profile.fullName")}</Label>
                <Input id="pf-fullname" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={200} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="pf-preferred">{t("profile.preferredName")}</Label>
                <Input id="pf-preferred" value={preferredName} onChange={(e) => setPreferredName(e.target.value)} maxLength={200} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="pf-lang">{t("profile.language")}</Label>
                <Select value={language} onValueChange={(v) => setLanguage(v as Lang)}>
                  <SelectTrigger id="pf-lang" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={save.isPending}>{save.isPending ? t("crud.saving") : t("common.save")}</Button>
                {save.isSuccess && <span className="text-sm text-emerald-600">{t("profile.saved")}</span>}
                {save.isError && <span className="text-sm text-destructive">{t("rep.failed")} {(save.error as Error).message}</span>}
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
